import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WorkflowBuilder = ({ campaign, onSave, onClose }) => {
  const [workflow, setWorkflow] = useState({
    nodes: [
      {
        id: 'trigger',
        type: 'trigger',
        position: { x: 100, y: 100 },
        data: { label: 'Birthday Trigger', icon: 'Calendar' }
      },
      {
        id: 'message1',
        type: 'message',
        position: { x: 100, y: 200 },
        data: { 
          label: 'Birthday Greeting',
          content: 'Happy Birthday! 🎉',
          delay: 0
        }
      },
      {
        id: 'wait1',
        type: 'wait',
        position: { x: 100, y: 300 },
        data: { label: 'Wait 7 days', delay: 7 }
      },
      {
        id: 'message2',
        type: 'message',
        position: { x: 100, y: 400 },
        data: { 
          label: 'Follow-up Message',
          content: 'Hope you had a great birthday!',
          delay: 7
        }
      }
    ],
    connections: [
      { from: 'trigger', to: 'message1' },
      { from: 'message1', to: 'wait1' },
      { from: 'wait1', to: 'message2' }
    ]
  });

  const [selectedNode, setSelectedNode] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);

  const nodeTypes = [
    { type: 'message', label: 'Send Message', icon: 'MessageSquare', color: 'bg-primary' },
    { type: 'wait', label: 'Wait/Delay', icon: 'Clock', color: 'bg-warning' },
    { type: 'condition', label: 'Condition', icon: 'GitBranch', color: 'bg-accent' },
    { type: 'action', label: 'Action', icon: 'Zap', color: 'bg-secondary' }
  ];

  const getNodeIcon = (type) => {
    switch (type) {
      case 'trigger':
        return 'Calendar';
      case 'message':
        return 'MessageSquare';
      case 'wait':
        return 'Clock';
      case 'condition':
        return 'GitBranch';
      case 'action':
        return 'Zap';
      default:
        return 'Circle';
    }
  };

  const getNodeColor = (type) => {
    switch (type) {
      case 'trigger':
        return 'bg-success';
      case 'message':
        return 'bg-primary';
      case 'wait':
        return 'bg-warning';
      case 'condition':
        return 'bg-accent';
      case 'action':
        return 'bg-secondary';
      default:
        return 'bg-muted';
    }
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  const handleAddNode = (type) => {
    const newNode = {
      id: `${type}_${Date.now()}`,
      type,
      position: { x: 100, y: workflow?.nodes?.length * 100 + 100 },
      data: {
        label: `New ${type}`,
        icon: getNodeIcon(type)
      }
    };

    setWorkflow(prev => ({
      ...prev,
      nodes: [...prev?.nodes, newNode]
    }));
  };

  const handleDeleteNode = (nodeId) => {
    setWorkflow(prev => ({
      nodes: prev?.nodes?.filter(node => node?.id !== nodeId),
      connections: prev?.connections?.filter(conn => conn?.from !== nodeId && conn?.to !== nodeId)
    }));
    setSelectedNode(null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex h-screen">
        {/* Sidebar */}
        <div className="w-80 bg-card border-r border-border flex flex-col">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Workflow Builder</h2>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Drag and drop elements to build your campaign workflow
            </p>
          </div>

          {/* Node Palette */}
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-medium text-foreground mb-3">Add Elements</h3>
            <div className="space-y-2">
              {nodeTypes?.map((nodeType) => (
                <button
                  key={nodeType?.type}
                  onClick={() => handleAddNode(nodeType?.type)}
                  className="w-full flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-muted transition-smooth"
                >
                  <div className={`w-8 h-8 ${nodeType?.color} rounded-lg flex items-center justify-center`}>
                    <Icon name={nodeType?.icon} size={16} color="white" />
                  </div>
                  <span className="text-sm font-medium text-foreground">{nodeType?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Node Properties */}
          {selectedNode && (
            <div className="flex-1 p-4 overflow-y-auto">
              <h3 className="text-sm font-medium text-foreground mb-3">Properties</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Node Label
                  </label>
                  <input
                    type="text"
                    value={selectedNode?.data?.label}
                    onChange={(e) => {
                      const updatedNode = {
                        ...selectedNode,
                        data: { ...selectedNode?.data, label: e?.target?.value }
                      };
                      setSelectedNode(updatedNode);
                      setWorkflow(prev => ({
                        ...prev,
                        nodes: prev?.nodes?.map(node => 
                          node?.id === selectedNode?.id ? updatedNode : node
                        )
                      }));
                    }}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {selectedNode?.type === 'message' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Message Content
                    </label>
                    <textarea
                      value={selectedNode?.data?.content || ''}
                      onChange={(e) => {
                        const updatedNode = {
                          ...selectedNode,
                          data: { ...selectedNode?.data, content: e?.target?.value }
                        };
                        setSelectedNode(updatedNode);
                        setWorkflow(prev => ({
                          ...prev,
                          nodes: prev?.nodes?.map(node => 
                            node?.id === selectedNode?.id ? updatedNode : node
                          )
                        }));
                      }}
                      rows={4}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter your message content..."
                    />
                  </div>
                )}

                {(selectedNode?.type === 'wait' || selectedNode?.type === 'message') && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Delay (days)
                    </label>
                    <input
                      type="number"
                      value={selectedNode?.data?.delay || 0}
                      onChange={(e) => {
                        const updatedNode = {
                          ...selectedNode,
                          data: { ...selectedNode?.data, delay: parseInt(e?.target?.value) || 0 }
                        };
                        setSelectedNode(updatedNode);
                        setWorkflow(prev => ({
                          ...prev,
                          nodes: prev?.nodes?.map(node => 
                            node?.id === selectedNode?.id ? updatedNode : node
                          )
                        }));
                      }}
                      min="0"
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}

                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeleteNode(selectedNode?.id)}
                  className="w-full"
                >
                  <Icon name="Trash2" size={16} />
                  Delete Node
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Canvas */}
        <div className="flex-1 bg-muted/20 relative overflow-hidden">
          <div className="absolute inset-0 p-8">
            <div className="relative w-full h-full">
              {/* Grid Background */}
              <div className="absolute inset-0 opacity-20">
                <svg width="100%" height="100%">
                  <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="1"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
              </div>

              {/* Workflow Nodes */}
              {workflow?.nodes?.map((node, index) => (
                <div key={node?.id} className="absolute">
                  <div
                    style={{
                      left: node?.position?.x,
                      top: node?.position?.y + index * 120
                    }}
                    onClick={() => handleNodeClick(node)}
                    className={`relative bg-card border-2 rounded-lg p-4 cursor-pointer transition-smooth hover:elevation-2 ${
                      selectedNode?.id === node?.id ? 'border-primary' : 'border-border'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 ${getNodeColor(node?.type)} rounded-lg flex items-center justify-center`}>
                        <Icon name={getNodeIcon(node?.type)} size={20} color="white" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{node?.data?.label}</div>
                        {node?.data?.delay > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {node?.data?.delay} day{node?.data?.delay !== 1 ? 's' : ''} delay
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Connection Line */}
                    {index < workflow?.nodes?.length - 1 && (
                      <div className="absolute left-1/2 -bottom-6 w-0.5 h-6 bg-border transform -translate-x-1/2">
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1">
                          <Icon name="ChevronDown" size={12} className="text-muted-foreground" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Canvas Controls */}
          <div className="absolute bottom-4 right-4 flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Icon name="ZoomIn" size={16} />
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="ZoomOut" size={16} />
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="Maximize" size={16} />
            </Button>
          </div>
        </div>
      </div>
      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 bg-card border-t border-border p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-muted-foreground">
              {workflow?.nodes?.length} nodes • {workflow?.connections?.length} connections
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => onSave(workflow)}>
              <Icon name="Save" size={16} />
              Save Workflow
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowBuilder;